import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

// Lightweight inline SVGs for different craft types
const Craft = ({ id, craftType, sizePx = 28, color = '#9AE6B4' }) => {
  const stroke = color;
  const fill = 'none';
  const strokeWidth = 1.5;

  // Normalize viewBox so sizePx scales uniformly
  return (
    <svg
      width={sizePx}
      height={sizePx}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        filter:
          'drop-shadow(0 0 2px rgba(255,255,255,0.25)) drop-shadow(0 0 6px rgba(146, 255, 193, 0.15))',
        pointerEvents: 'none',
        transition: 'none',
      }}
    >
      <defs>
        {/* UFO beam gradient */}
        <radialGradient id={`beamGradient-${id}`} cx="50%" cy="30%" r="80%">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="60%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
        {/* Engine plume gradient */}
        <linearGradient id={`engineGradient-${id}`} x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.0" />
          <stop offset="40%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0.0" />
        </linearGradient>
      </defs>
      {craftType === 'ufo' && (
        <g>
          {/* Beam (behind craft) */}
          <polygon
            className="beam-flicker"
            points="42,57 58,57 70,92 30,92"
            fill={`url(#beamGradient-${id})`}
          />
          {/* Dome */}
          <path d="M30 45 C 40 28, 60 28, 70 45" stroke={stroke} strokeWidth={strokeWidth} fill={fill} />
          {/* Saucer */}
          <ellipse cx="50" cy="55" rx="30" ry="10" stroke={stroke} strokeWidth={strokeWidth} fill={fill} />
          {/* Windows */}
          <circle cx="40" cy="55" r="2.2" fill={stroke} />
          <circle cx="50" cy="55" r="2.2" fill={stroke} />
          <circle cx="60" cy="55" r="2.2" fill={stroke} />
          {/* Subtle beam hint */}
          <path d="M43 65 L57 65" stroke={stroke} strokeOpacity="0.5" strokeWidth={1} />
        </g>
      )}
      {craftType === 'ship' && (
        <g>
          {/* Engine plume (behind) */}
          <polygon
            className="engine-flicker"
            points="25,62 15,60 25,58"
            fill={`url(#engineGradient-${id})`}
          />
          {/* Body */}
          <path d="M25 60 L50 40 L75 60 L50 70 Z" stroke={stroke} strokeWidth={strokeWidth} fill={fill} />
          {/* Cockpit */}
          <path d="M47 48 L53 48 L50 45 Z" stroke={stroke} strokeWidth={strokeWidth} fill={fill} />
          {/* Fins */}
          <path d="M35 62 L30 67" stroke={stroke} strokeWidth={strokeWidth} />
          <path d="M65 62 L70 67" stroke={stroke} strokeWidth={strokeWidth} />
        </g>
      )}
      {craftType === 'probe' && (
        <g>
          {/* Soft thrust glow */}
          <circle className="engine-flicker" cx="40" cy="60" r="6" fill={`url(#engineGradient-${id})`} />
          {/* Core */}
          <circle cx="50" cy="50" r="10" stroke={stroke} strokeWidth={strokeWidth} fill={fill} />
          {/* Antenna */}
          <path d="M50 40 L50 30" stroke={stroke} strokeWidth={strokeWidth} />
          <circle cx="50" cy="28" r="2" fill={stroke} />
          {/* Solar panels */}
          <rect x="25" y="48" width="15" height="4" stroke={stroke} strokeWidth={strokeWidth} fill={fill} />
          <rect x="60" y="48" width="15" height="4" stroke={stroke} strokeWidth={strokeWidth} fill={fill} />
        </g>
      )}
    </svg>
  );
};

// Generate a distribution that yields more mid-depth crafts and fewer near/far
function sampleDepth() {
  const r = Math.random();
  if (r < 0.2) return 0.10 + Math.random() * 0.06; // very far 0.10 - 0.16
  if (r < 0.8) return 0.18 + Math.random() * 0.18; // mid 0.18 - 0.36
  return 0.38 + Math.random() * 0.14; // near 0.38 - 0.52
}

const craftTypes = ['ufo', 'ship', 'probe'];

const SpaceTraffic = () => {
  const { isDark } = useTheme();
  const [crafts, setCrafts] = useState([]);
  const craftRefs = useRef([]);
  const worldCraftsRef = useRef([]); // mutable physics state
  const rafIdRef = useRef(null);
  const lastTsRef = useRef(0);
  const latestScrollYRef = useRef(0);
  const viewportRef = useRef({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  const palette = useMemo(() => {
    return isDark
      ? ['#9AE6B4', '#90CDF4', '#FBB6CE']
      : ['#4A5568', '#2D3748', '#718096'];
  }, [isDark]);

  useEffect(() => {
    // Initialize crafts
    const initCrafts = () => {
      const count = 4; // reduced traffic density for realism and clarity
      const list = [];
      const worldList = [];
      const vw = viewportRef.current.width;
      const vh = viewportRef.current.height;
      const spawnMarginX = vw * 0.2;

      for (let i = 0; i < count; i += 1) {
        const depth = sampleDepth();
        const type = craftTypes[Math.floor(Math.random() * craftTypes.length)];
        const baseSize = type === 'probe' ? 18 : type === 'ufo' ? 26 : 24;
        const scale = 0.7 + depth * 0.9; // larger when nearer
        const sizePx = Math.round(baseSize * scale);

        // Randomize left-to-right or right-to-left
        const leftToRight = Math.random() < 0.6;
        const speedBase = 10 + depth * 28; // toned-down base speed
        const speedJitter = 0.8 + Math.random() * 0.6; // 0.8 - 1.4
        const speed = speedBase * speedJitter;

        const headingDeg = leftToRight ? (Math.random() * 20 - 10) : (180 + Math.random() * 20 - 10); // slight vertical drift
        const headingRad = (headingDeg * Math.PI) / 180;

        const worldX = leftToRight ? -spawnMarginX - Math.random() * vw * 0.4 : vw + spawnMarginX + Math.random() * vw * 0.4;
        const worldY = -vh * 0.5 + Math.random() * vh * 2.0; // spread vertically

        const color = palette[Math.floor(Math.random() * palette.length)];

        // Base Z (used to modulate scale/blur/speed). Around 1.0 with slight variance.
        const baseZ = 0.9 + Math.random() * 0.5; // 0.9 - 1.4

        list.push({ id: i, depth, type, sizePx, color, baseZ });
        worldList.push({
          id: i,
          depth,
          // world space position
          x: worldX,
          y: worldY,
          // velocity
          vx: Math.cos(headingRad) * speed,
          vy: Math.sin(headingRad) * speed * 0.6, // tone down vertical drift
          // orientation for visual rotation in degrees
          rotationDeg: headingDeg + (leftToRight ? 0 : 180),
          // gentle sway
          swayPhase: Math.random() * Math.PI * 2,
          swayAmpDeg: 2 + Math.random() * 4,
          // Z-motion (toward/away from camera)
          zPhase: Math.random() * Math.PI * 2,
          zAmp: 0.08 + Math.random() * 0.12, // reduced amplitude of z oscillation
          zOmega: 0.2 + Math.random() * 0.25, // slower z oscillation
          // Course noise (subtle heading variation)
          coursePhase: Math.random() * Math.PI * 2,
          courseOmega: 0.15 + Math.random() * 0.2,
          // lifetime recycling
          leftToRight,
        });
      }

      setCrafts(list);
      worldCraftsRef.current = worldList;
    };

    initCrafts();
  }, [palette]);

  useEffect(() => {
    const getScrollY = () =>
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    const applyTransforms = (dtSec) => {
      const vw = viewportRef.current.width;
      const vh = viewportRef.current.height;
      const xParallaxBase = 0.02; // keep in sync with StarField for consistency
      const yParallaxBase = 0.10;
      const yDepthMult = 0.20;
      const scrollY = latestScrollYRef.current;

      for (let i = 0; i < worldCraftsRef.current.length; i += 1) {
        const craft = worldCraftsRef.current[i];
        const el = craftRefs.current[craft.id];
        if (!craft || !el) continue;

        // Z motion update (simulate approaching/receding)
        craft.zPhase += craft.zOmega * dtSec;
        const zLocal = Math.sin(craft.zPhase) * craft.zAmp; // -amp..amp
        // Effective Z around ~1.0 ± amp, clamped to reasonable bounds
        const baseZ = (typeof el.__baseZ === 'number' ? el.__baseZ : null);
        const currentBaseZ = baseZ != null ? baseZ : 1.0;
        const effectiveZ = Math.min(1.8, Math.max(0.55, currentBaseZ + zLocal));

        // Course noise (subtle heading variation)
        craft.coursePhase += craft.courseOmega * dtSec;
        const angleJitterRad = Math.sin(craft.coursePhase) * 0.015; // ~0.86° sway
        const speedMag = Math.hypot(craft.vx, craft.vy);
        const currentAngle = Math.atan2(craft.vy, craft.vx);
        const newAngle = currentAngle + angleJitterRad;
        craft.vx = Math.cos(newAngle) * speedMag;
        craft.vy = Math.sin(newAngle) * speedMag;

        // Integrate physics with Z-based speed amplification (near feels faster)
        const speedScale = 0.6 + effectiveZ * 0.4; // slower overall
        craft.x += craft.vx * dtSec * speedScale;
        craft.y += craft.vy * dtSec * speedScale;
        craft.swayPhase += dtSec * 0.6; // sway speed
        const swayDeg = Math.sin(craft.swayPhase) * craft.swayAmpDeg;

        // Recycle if out of extended bounds
        const outLeft = craft.x < -vw * 0.6;
        const outRight = craft.x > vw * 1.6;
        if (outLeft || outRight) {
          // respawn at opposite side with similar params
          const depth = craft.depth;
          const leftToRight = Math.random() < 0.6;
          const speedBase = 18 + depth * 60;
          const speed = speedBase * (0.8 + Math.random() * 0.6);
          const headingDeg = leftToRight ? (Math.random() * 20 - 10) : (180 + Math.random() * 20 - 10);
          const headingRad = (headingDeg * Math.PI) / 180;
          craft.x = leftToRight ? -vw * 0.3 : vw * 1.3;
          craft.y = -vh * 0.5 + Math.random() * vh * 2.0;
          craft.vx = Math.cos(headingRad) * speed;
          craft.vy = Math.sin(headingRad) * speed * 0.6;
          craft.rotationDeg = headingDeg + (leftToRight ? 0 : 180);
          craft.leftToRight = leftToRight;
          // Reset Z and noise
          craft.zPhase = Math.random() * Math.PI * 2;
          craft.zAmp = 0.18 + Math.random() * 0.25;
          craft.zOmega = 0.3 + Math.random() * 0.4;
          craft.coursePhase = Math.random() * Math.PI * 2;
          craft.courseOmega = 0.15 + Math.random() * 0.2;
        }

        // Camera parallax relative to scroll
        const deltaY = scrollY * (yParallaxBase + craft.depth * yDepthMult);
        const translateXParallax = scrollY * (craft.depth - 0.5) * xParallaxBase;

        // Screen-space position
        const screenX = craft.x + translateXParallax;
        const screenY = craft.y - deltaY;

        // Visual scale and blur by Z
        const scale = 0.85 + effectiveZ * 0.25; // smaller scale variance
        const blurPx = Math.max(0, (1.0 - Math.min(1.4, effectiveZ)) * 2.0); // farther -> blurrier
        const brightness = 1.0 * (isNaN(blurPx) ? 1 : 1); // neutral brightness; could tweak by theme

        // Apply transform and effects
        el.style.transform = `translate3d(${screenX.toFixed(2)}px, ${screenY.toFixed(2)}px, 0) rotate(${(craft.rotationDeg + swayDeg).toFixed(2)}deg) scale(${scale.toFixed(3)})`;
        el.style.filter = `blur(${blurPx.toFixed(2)}px) brightness(${brightness})`;
        el.style.opacity = String(Math.min(1, 0.55 + effectiveZ * 0.35));
      }
    };

    const loop = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dtSec = Math.min(0.05, Math.max(0.001, (ts - lastTsRef.current) / 1000)); // clamp dt for stability
      lastTsRef.current = ts;
      latestScrollYRef.current = getScrollY();
      applyTransforms(dtSec);
      rafIdRef.current = window.requestAnimationFrame(loop);
    };

    // Initialize
    latestScrollYRef.current = getScrollY();
    rafIdRef.current = window.requestAnimationFrame(loop);

    const handleResize = () => {
      viewportRef.current = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    };
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      if (rafIdRef.current != null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0" aria-hidden>
      {/* We directly manipulate transforms of these absolutely positioned holders */}
      {crafts.map((c) => (
        <div
          key={c.id}
          ref={(el) => {
            if (el) {
              craftRefs.current[c.id] = el;
              // Ensure transforms are not animated by global CSS transitions
              el.style.willChange = 'transform';
              el.style.transition = 'none';
              el.style.position = 'absolute';
              el.style.left = '0px';
              el.style.top = '0px';
              el.style.transformOrigin = '50% 50%';
              // save baseZ to element for stable per-instance base
              if (typeof el.__baseZ !== 'number') {
                el.__baseZ = c.baseZ || 1.0;
              }
            }
          }}
          className="transform-gpu"
          style={{}}
        >
          <Craft id={c.id} craftType={c.type} sizePx={c.sizePx} color={c.color} />
        </div>
      ))}
    </div>
  );
};

export default SpaceTraffic;


